import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiClient';

interface UsePreviousPeriodDataProps {
  config: any;
  storeId: string | null;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

// Función para calcular las fechas del período anterior
const getPreviousPeriodDates = (startDate: string, endDate: string) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const duration = end.getTime() - start.getTime();
  
  const previousEnd = new Date(start);
  const previousStart = new Date(previousEnd.getTime() - duration);
  
  return {
    previousStart: previousStart.toISOString().split('T')[0],
    previousEnd: previousEnd.toISOString().split('T')[0]
  };
};

export const usePreviousPeriodData = ({ config, storeId, startDate, endDate, enabled = true }: UsePreviousPeriodDataProps) => {
  return useQuery({
    queryKey: [`previous-${config.id}`, storeId, startDate, endDate],
    queryFn: async () => {
      if (!storeId || !startDate || !endDate) {
        console.log(`⚠️ Missing required params for previous data:`, { storeId, startDate, endDate });
        return { value: 0 };
      }
      
      const { previousStart, previousEnd } = getPreviousPeriodDates(startDate, endDate);
      let previousEndpoint = config.apiEndpoint.replace('{storeId}', storeId);
      const url = new URL(previousEndpoint, process.env.REACT_APP_API_URL || '');
      url.searchParams.set('startDate', previousStart);
      url.searchParams.set('endDate', previousEnd);
      previousEndpoint = url.pathname + url.search;
      
      console.log(`🔍 Fetching previous data from: ${previousEndpoint}`);
      console.log(`📅 Previous period: ${previousStart} to ${previousEnd}`);
      
      try {
        const previousResponse = await apiService.get(previousEndpoint);
        const previousData = previousResponse.data;
        
        console.log(`📥 Previous data received for ${config.id}:`, previousData);
        
        // Validar que los datos sean válidos
        if (!previousData) {
          console.warn(`⚠️ No previous data received for ${config.id}`);
          return { value: 0 };
        }
        
        // Si es un array, validar cada elemento
        if (Array.isArray(previousData)) {
          const validData = previousData.filter(item => {
            const value = item?.value || 0;
            return !isNaN(value) && isFinite(value) && value >= 0;
          });
          
          if (validData.length === 0) {
            console.warn(`⚠️ No valid previous data in array for ${config.id}`);
            return { value: 0 };
          }
          
          return validData;
        }
        
        // Si es un objeto con value, validar el value
        if ((previousData as any).value !== undefined) {
          const value = (previousData as any).value;
          if (isNaN(value) || !isFinite(value)) {
            console.warn(`⚠️ Invalid previous value for ${config.id}:`, value);
            return { value: 0 };
          }
        }
        
        return previousData;
      } catch (error) {
        console.warn(`⚠️ Error fetching previous data for ${config.id}:`, error);
        return { value: 0 };
      }
    },
    enabled: enabled && !!storeId && !!config.apiEndpoint && !!startDate && !!endDate,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
    staleTime: 15 * 60 * 1000, // Los datos se consideran frescos por 15 minutos
    gcTime: 20 * 60 * 1000, // Mantener en caché por 20 minutos
  });
};
